import React, { useMemo, useState } from 'react';
import { useGetCategoryWiseComplaintsQuery } from '../../redux/complaintApi';
import { SearchableSelect } from './SearchableSelect';
import { Category_STATUS_SELECT_OPTIONS } from '../../utils/complaintStatus';

const STATUS_FILTER_OPTIONS = [
  { label: 'All', value: 'all', apiFilter: null },
  ...Category_STATUS_SELECT_OPTIONS.map(({ value, label }) => ({
    label,
    value,
    apiFilter: value,
  })),
];

const filterSelectClasses =
  'w-full rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-xs font-medium text-gray-800 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

const COLOR_CLASSES = [
  'bg-yellow-400',
  'bg-rose-400',
  'bg-amber-500',
  'bg-violet-400',
  'bg-emerald-400',
  'bg-sky-400',
  'bg-orange-400',
  'bg-pink-400',
];

const BAR_COLOR_CLASSES = [
  'bg-yellow-500',
  'bg-rose-500',
  'bg-amber-600',
  'bg-violet-500',
  'bg-emerald-500',
  'bg-sky-500',
  'bg-orange-500',
  'bg-pink-500',
];

const toNumberOrNull = (v) => {
  if (v === undefined || v === null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const normalizeCategoryWise = (payload) => {
  const raw = payload?.data ?? payload;

  let rows = [];

  if (Array.isArray(raw)) {
    rows = raw;
  } else if (raw && typeof raw === 'object') {
    const maybeList =
      raw.categories || raw.category_wise || raw.categoryWise || raw.result;
    if (Array.isArray(maybeList)) {
      rows = maybeList;
    } else {
      rows = Object.entries(raw).map(([k, v]) => ({ name: k, count: v }));
    }
  }

  const normalized = rows
    .map((r) => {
      const name =
        r?.category_name ??
        r?.category ??
        r?.name ??
        r?.label ??
        '';

      const count =
        toNumberOrNull(r?.complaints_count) ??
        toNumberOrNull(r?.count) ??
        toNumberOrNull(r?.value) ??
        0;

      const percent =
        toNumberOrNull(r?.percentage) ??
        toNumberOrNull(r?.percent) ??
        null;

      return {
        name: String(name || '—'),
        count,
        percent,
      };
    })
    .filter((r) => r.name && r.name !== '—');

  const totalCount = normalized.reduce((sum, r) => sum + (r.count || 0), 0);
  return normalized
    .map((r) => ({
      ...r,
      percent:
        r.percent != null
          ? r.percent
          : totalCount > 0
            ? (r.count / totalCount) * 100
            : 0,
    }))
    .sort((a, b) => (b.count || 0) - (a.count || 0));
};

function CategoryComplaints() {
  const [statusFilter, setStatusFilter] = useState('all');

  const apiFilters = useMemo(() => {
    const opt = STATUS_FILTER_OPTIONS.find((o) => o.value === statusFilter);
    return opt?.apiFilter ? [opt.apiFilter] : [];
  }, [statusFilter]);

  const { data, isLoading, isFetching } = useGetCategoryWiseComplaintsQuery(apiFilters);
  const categories = normalizeCategoryWise(data);

  return (
    <div className="flex h-full max-h-[420px] flex-col rounded-xl bg-white p-4 shadow-sm sm:max-h-[380px] sm:p-6">
      <div className="mb-4 shrink-0 border-b border-gray-100 pb-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-base font-semibold text-gray-900">Category wise Complaints</h3>
          <div className="w-full sm:w-36">
            <SearchableSelect
              name="statusFilter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={STATUS_FILTER_OPTIONS.map(({ label, value }) => ({ label, value }))}
              placeholder="All"
              clearable={false}
              className={filterSelectClasses}
            />
          </div>
        </div>
      </div>

      <ul className="min-h-0 flex-1 divide-y divide-gray-100 overflow-y-auto overscroll-y-contain">
        {isLoading || isFetching ? (
          <li className="py-8 text-center text-sm text-gray-500">Loading…</li>
        ) : categories.length === 0 ? (
          <li className="py-8 text-center text-sm text-gray-500">No data for this filter</li>
        ) : (
          categories.map((cat, i) => {
            const colorIdx = i % COLOR_CLASSES.length;
            const percent = Number(cat.percent || 0);
            const barWidth = Math.min(Math.max(percent, 0), 100);

            return (
              <li key={`${cat.name}-${i}`} className="py-3 first:pt-0">
                <div className="flex items-start gap-2.5 sm:gap-3">
                  <span
                    className={`mt-0.5 h-3 w-3 shrink-0 rounded-sm sm:mt-1 sm:h-3.5 sm:w-3.5 ${COLOR_CLASSES[colorIdx]}`}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <span className="min-w-0 flex-1 text-sm font-medium leading-snug text-gray-800">
                        {cat.name}
                      </span>
                      <span className="shrink-0 text-sm font-bold tabular-nums text-brand">
                        {percent.toFixed(2)}%
                      </span>
                    </div>
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#f3eee8]">
                      <div
                        className={`h-full rounded-full transition-all ${BAR_COLOR_CLASSES[colorIdx]}`}
                        style={{ width: `${barWidth}%` }}
                      />
                    </div>
                    {cat.count > 0 ? (
                      <p className="mt-1 text-[10px] text-gray-500 sm:text-xs">
                        {cat.count} complaint{cat.count === 1 ? '' : 's'}
                      </p>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
}

export default CategoryComplaints;
